import { filterPeople } from "./peopleSearch.js";
import {
    DELETED_INVITE_STATUS,
    REVOKED_INVITE_STATUS,
} from "../../../../Composables/useTeamMembers";

function isRevokedInviteProfile(profile) {
    return Number(profile?.invite_status) === REVOKED_INVITE_STATUS;
}

function isDeletedInviteProfile(profile) {
    return Number(profile?.invite_status) === DELETED_INVITE_STATUS;
}

function isReinvitableOrganizationInviteProfile(profile) {
    if (profile?.type !== "business" || profile?.is_agent) {
        return false;
    }

    return isRevokedInviteProfile(profile) || isDeletedInviteProfile(profile);
}

export function normalizeMembershipSearchEmail(email) {
    return String(email ?? "").trim().toLowerCase();
}

export function isExistingWorkPanelOrganizationEmail(profiles, email) {
    const normalized = normalizeMembershipSearchEmail(email);

    if (!normalized) {
        return false;
    }

    return (profiles ?? []).some((profile) => {
        if (normalizeMembershipSearchEmail(profile?.email) !== normalized) {
            return false;
        }

        if (
            profile?.is_organization_user === false &&
            isReinvitableOrganizationInviteProfile(profile)
        ) {
            return false;
        }

        return true;
    });
}

export function collectDirectoryInviteEmails(...profileLists) {
    return new Set(
        profileLists
            .flat()
            .filter(
                (profile) =>
                    profile?.is_organization_user === false &&
                    !isDeletedInviteProfile(profile),
            )
            .map((profile) => normalizeMembershipSearchEmail(profile?.email))
            .filter(Boolean),
    );
}

export function filterInlineSearchProfilesAgainstDirectory(
    inlineProfiles,
    directoryPeople,
    directoryInviteEmails = null,
) {
    const directoryEmails = new Set(
        (directoryPeople ?? [])
            .map((profile) => normalizeMembershipSearchEmail(profile?.email))
            .filter(Boolean),
    );

    const inviteEmails =
        directoryInviteEmails instanceof Set
            ? directoryInviteEmails
            : collectDirectoryInviteEmails(directoryPeople);

    inviteEmails.forEach((email) => directoryEmails.add(email));

    return (inlineProfiles ?? []).filter((profile) => {
        const email = normalizeMembershipSearchEmail(profile?.email);

        if (!email) {
            return true;
        }

        if (isReinvitableOrganizationInviteProfile(profile)) {
            return !inviteEmails.has(email);
        }

        return !directoryEmails.has(email);
    });
}

function countMatchingGroups(groups, searchQuery) {
    const query = String(searchQuery).trim();

    if (!query) {
        return 0;
    }

    return (groups ?? []).filter((group) =>
        group?.name?.toLowerCase().includes(query.toLowerCase()),
    ).length;
}

export function countVisibleAllTabSearchResults({
    associatedPeople = [],
    disassociatedPeople = [],
    inlineSearchProfiles = [],
    pendingInvites = [],
    revokedInvites = [],
    groups = [],
    topics = [],
    searchQuery = "",
}) {
    if (!String(searchQuery).trim()) {
        return 0;
    }

    const directoryPeople = [...associatedPeople, ...disassociatedPeople];
    const inviteEmails = collectDirectoryInviteEmails(
        pendingInvites,
        revokedInvites,
    );
    const dedupedInline = filterInlineSearchProfilesAgainstDirectory(
        inlineSearchProfiles,
        directoryPeople,
        inviteEmails,
    );

    return (
        filterPeople(associatedPeople, searchQuery).length +
        filterPeople(disassociatedPeople, searchQuery).length +
        filterPeople(pendingInvites, searchQuery).length +
        filterPeople(revokedInvites, searchQuery).length +
        filterPeople(dedupedInline, searchQuery).length +
        countMatchingGroups(groups, searchQuery) +
        countMatchingGroups(topics, searchQuery)
    );
}

export function countMembershipPanelSearchResults({
    associatedPeople = [],
    disassociatedPeople = [],
    pendingInvites = [],
    canShowInvites = false,
    searchQuery = "",
}) {
    if (!String(searchQuery).trim()) {
        return 0;
    }

    return (
        filterPeople(associatedPeople, searchQuery).length +
        (canShowInvites
            ? filterPeople(pendingInvites, searchQuery).length
            : 0) +
        filterPeople(disassociatedPeople, searchQuery).length
    );
}

export function findStaleInlineSearchProfileIds(
    inlineProfiles,
    directoryPeople,
    directoryInviteEmails = null,
) {
    const directoryEmails = new Set(
        (directoryPeople ?? [])
            .map((profile) => normalizeMembershipSearchEmail(profile?.email))
            .filter(Boolean),
    );

    const inviteEmails =
        directoryInviteEmails instanceof Set
            ? directoryInviteEmails
            : collectDirectoryInviteEmails(directoryPeople);

    inviteEmails.forEach((email) => directoryEmails.add(email));

    return (inlineProfiles ?? [])
        .filter((profile) => {
            const email = normalizeMembershipSearchEmail(profile?.email);

            if (!email) {
                return false;
            }

            if (isReinvitableOrganizationInviteProfile(profile)) {
                return inviteEmails.has(email);
            }

            return directoryEmails.has(email);
        })
        .map((profile) => profile.id);
}

/**
 * Pick the profile the product tour should auto-select / assign.
 * When a search query is active, only consider profiles that match it
 * (the same set the membership list shows), so a different associated
 * member cannot be selected while hidden by the filter.
 *
 * @param {object} params
 * @param {Array} [params.associatedPeople]
 * @param {Array} [params.disassociatedPeople]
 * @param {Array} [params.inlineSearchProfiles]
 * @param {Array} [params.chinMembers]
 * @param {Array} [params.participants]
 * @param {Array<number|string>} [params.ownerIds]
 * @param {string} [params.searchQuery]
 * @returns {object|null}
 */
export function findTourAssignableProfile({
    associatedPeople = [],
    disassociatedPeople = [],
    inlineSearchProfiles = [],
    chinMembers = [],
    participants = [],
    ownerIds = [],
    searchQuery = "",
} = {}) {
    const ownerIdSet = new Set((ownerIds ?? []).map((id) => Number(id)));
    const query = String(searchQuery ?? "").trim();

    const candidates = query
        ? [
              ...filterPeople(associatedPeople, query),
              ...filterPeople(inlineSearchProfiles, query),
              ...filterPeople(disassociatedPeople, query),
              ...filterPeople(chinMembers ?? [], query),
              ...filterPeople(participants ?? [], query),
          ]
        : [
              ...(associatedPeople ?? []),
              ...(inlineSearchProfiles ?? []),
              ...(chinMembers ?? []),
              ...(participants ?? []),
          ];

    return (
        candidates.find((profile) => {
            if (!profile?.id || profile.is_agent) {
                return false;
            }

            return !ownerIdSet.has(Number(profile.id));
        }) ?? null
    );
}

export function shouldFetchWorkPanelInviteSearch({
    email,
    profiles,
    invitedEmails = [],
    inlineProfiles = [],
    directoryPeople = [],
    pendingInvites = [],
    revokedInvites = [],
    canInvite = false,
    isValidEmail = false,
}) {
    const normalized = normalizeMembershipSearchEmail(email);

    if (!normalized || !canInvite || !isValidEmail) {
        return false;
    }

    if (isExistingWorkPanelOrganizationEmail(profiles, normalized)) {
        return false;
    }

    if (
        (invitedEmails ?? []).some(
            (invitedEmail) =>
                normalizeMembershipSearchEmail(invitedEmail) === normalized,
        )
    ) {
        return false;
    }

    if (
        (inlineProfiles ?? []).some(
            (profile) =>
                normalizeMembershipSearchEmail(profile?.email) === normalized,
        )
    ) {
        return false;
    }

    const inviteEmails = collectDirectoryInviteEmails(
        pendingInvites,
        revokedInvites,
    );

    if (inviteEmails.has(normalized)) {
        return false;
    }

    if (filterPeople(directoryPeople, email).length > 0) {
        return false;
    }

    return true;
}
